import { ChangeEvent, useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { getKnmFormRef, getKnmSystemType } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { addNoticeKnmFileSizeErrorDetailed } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';

import styles from './ErknmServiceDocument.module.scss';
import { getMaxFileSize } from '../../../CorePackage/MainModule/common/getMaxFileSize';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { MB } from '../../../CorePackage/MainModule/constants/fileSize';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';

interface IErknmServiceDocumentProps {
  attachmentsNumber: number;
  watchOnly: boolean;
}

interface IErknmServiceDocumentStoreProps {
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[];
  knmSystemType: KnmSystemTypesEnum | undefined;
}

const ErknmServiceDocumentWithoutStore = ({
  attachmentsNumber,
  watchOnly,
  formRef,
  settings,
  knmSystemType,
}: IErknmServiceDocumentProps & IErknmServiceDocumentStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch();
  const { downloadKnmDocumentAttachment, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const isAddFileDisabled = watchOnly || attachmentsNumber > 1;
  const addAttachment = (fieldName: string, data: IKnmFormAttachment) => !watchOnly && push && push(fieldName, data);
  const makeRemoveAttachmentHandler = (index: number) => () =>
    !watchOnly && remove && remove('serviceDocument.attachments', index);

  const maxFileSize = getMaxFileSize(settings, knmSystemType);

  const handleFileSizeError = useCallback(() => {
    dispatch(addNoticeKnmFileSizeErrorDetailed(maxFileSize));
  }, [dispatch, maxFileSize]);

  const makeFileInputChangeHandler = () => async (e: ChangeEvent<HTMLInputElement>) => {
    if (isAddFileDisabled) {
      return;
    }
    const { files } = e.target;

    if (files) {
      const file = files[0];

      const isSizeValid = file.size <= maxFileSize * MB;

      if (!isSizeValid) {
        handleFileSizeError();
      } else {
        const listId = uuidv4();
        const newAttachment: IKnmFormAttachment = {
          fileName: file.name,
          fileSize: Math.trunc(file.size / 1000) || 0,
          signatureInfo: null,
          isNew: true,
          listId,
          file,
        };

        addAttachment('serviceDocument.attachments', newAttachment);
        const uploadedAttachment = await uploadKnmDocumentAttachment(file);

        if (uploadedAttachment) {
          const serviceDocumentAttachments = formRef?.current.getState().values.serviceDocument?.attachments;
          const uploadedAttachmentIndex = serviceDocumentAttachments?.findIndex(item => item.listId === listId);

          if (typeof uploadedAttachmentIndex === 'number' && uploadedAttachmentIndex >= 0) {
            const attachmentFieldName = `serviceDocument.attachments[${uploadedAttachmentIndex}]` as keyof IKnmForm;
            const newUploadedAttachment: IKnmFormAttachment = {
              ...uploadedAttachment,
              isNew: true,
            };

            formRef?.current.change(attachmentFieldName, newUploadedAttachment);
          }
        }
      }
    }
    e.target.value = '';
  };

  const downloadAttachment = (downloadingAttachment: IKnmFormAttachment) => () => {
    const { fileName, filePath, fileSize, mimeType } = downloadingAttachment;

    if (filePath && mimeType) {
      downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
    }
  };

  const acts = formRef?.current.getFieldState('acts')?.value || [];
  const hasActs = acts.length > 0;

  const addFileButtonAttributes = {
    ...((isAddFileDisabled || hasActs) && { 'data-disabled': true }),
  };

  return (
    <Row title="Информация о результате экспертизы продукции (товаров), результатов выполненных работ, оказанных услуг">
      <FieldBlock label="Описание" color="black">
        <ReactQuillEditorField
          id="serviceDocument.description"
          name="serviceDocument.descriptionReactQuillEditor"
          disabled={watchOnly || hasActs}
          handleCustomChange={(e: string) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formRef?.current?.change('serviceDocument.description' as any, e);
          }}
        />
      </FieldBlock>
      <Row>
        <div className={styles.AttachmentsTitleBlock} id="fileAddButton">
          <span className="shared-field-label">Файлы</span>
          <input
            id="serviceDocument.file"
            name="serviceDocument.file"
            type="file"
            accept=".ods,.xls,.xlsx,.doc,.docx,.pdf"
            style={{ display: 'none' }}
            disabled={isAddFileDisabled || hasActs}
            onChange={makeFileInputChangeHandler()}
          />
          <label
            className={cn('shared-btn-add', styles.AddFileButton)}
            htmlFor={!isAddFileDisabled && !hasActs ? 'serviceDocument.file' : undefined}
            {...addFileButtonAttributes}
          >
            Добавить
          </label>
        </div>
      </Row>

      <FieldArray name="serviceDocument.attachments">
        {({ fields: attachmentsFields }: FieldArrayRenderProps<IKnmFormAttachment, HTMLElement>) => {
          if (!attachmentsFields.length) {
            return null;
          }

          return (
            <ul className={styles.AttachmentsList}>
              {attachmentsFields.map((attachmentFieldName, attachmentIndex) => {
                const attachment = attachmentsFields.value?.[attachmentIndex];
                let progress: number | undefined;

                if (attachment.isNew) {
                  progress = attachment.dateCreate ? 100 : 0;
                }

                return (
                  <li className={styles.AttachmentsListItem} key={attachmentFieldName}>
                    <UploadFile
                      fileName={attachment.fileName || ''}
                      fileSize={attachment.fileSize}
                      progress={progress}
                      onFileNameClick={attachment.isNew ? undefined : downloadAttachment(attachment)}
                      onRemove={makeRemoveAttachmentHandler(attachmentIndex)}
                    />
                  </li>
                );
              })}
            </ul>
          );
        }}
      </FieldArray>
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IErknmServiceDocumentStoreProps => ({
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
  knmSystemType: getKnmSystemType(state),
});

export const ErknmServiceDocument = connect(mapStateToProps)(ErknmServiceDocumentWithoutStore);
